/* FDM site-wide code-format fallback, 14 October 2026.
 * Preserve original matches. Try alternate formats only when no products match.
 * A public-code lookup loads lazily; Shopify still controls returned products.
 * No third-party service, product-data edits or per-brand rules.
 */
window.FDMSearch = window.FDMSearch || (() => {
  function candidates(value) {
    const original = value.trim();
    // Preserve advanced search expressions and exact quoted phrases.
    if (/[:*"()]|\b(?:AND|OR|NOT)\b/.test(original)) return [original];
    const clean = original.normalize('NFKC').replace(/[\u2010-\u2015\u2212]/g, '-');
    const hyphenated = clean.replace(/\b([a-z]{1,12})[\s.-]*(\d+[a-z]*)\b/gi, '$1-$2');
    const spaced = hyphenated.replace(/[.-]+/g, ' ').replace(/\s+/g, ' ').trim();
    const joined = clean.replace(/\b([a-z]{1,12})[\s.-]+(?=\d)/gi, '$1').replace(/([a-z0-9])[.-]+(?=[a-z0-9])/gi, '$1');
    const seen = new Set();
    return [original, hyphenated, spaced, joined].filter(query => {
      const key = query.toLowerCase();
      if (!query || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  const indexPromises = new Map();
  const compact = value => value.normalize('NFKC').toLowerCase().replace(/[^a-z0-9]/g, '');

  function prefixQuery(query, length) {
    let letters = 0;
    for (let i = 0; i < query.length; i++) {
      if (/[a-z0-9]/i.test(query[i])) letters++;
      if (letters === length) return query.slice(0, i + 1);
    }
    return query;
  }

  function catalogueCandidates(value, index) {
    const key = compact(value);
    if (key.length < 3 || !/\d/.test(key) || /[:*"()]|\b(?:AND|OR|NOT)\b/.test(value)) return [];
    const entries = index.entries;
    let low = 0, high = entries.length;
    while (low < high) {
      const middle = (low + high) >>> 1;
      if (entries[middle][0] < key) low = middle + 1;
      else high = middle;
    }
    const found = [];
    // Exact aliases precede prefix aliases; distinct matches are capped below.
    for (let i = low; i < entries.length && entries[i][0].startsWith(key); i++) {
      const [alias, query] = entries[i];
      const targetKey = compact(query);
      if (alias === key) found.push(query);
      else if (targetKey.startsWith(key)) found.push(prefixQuery(query, key.length));
      if (found.length >= 100) break;
    }
    return [...new Set(found)].slice(0, 4);
  }

  function loadIndex(url) {
    if (!url) return Promise.resolve(null);
    if (!indexPromises.has(url)) {
      indexPromises.set(url, fetchText(url).then(text => {
        const index = JSON.parse(text);
        if (index.version !== 1 || !Array.isArray(index.entries)) throw new Error('Invalid search lookup');
        return index;
      }).catch(() => null));
    }
    return indexPromises.get(url);
  }

  async function alternateQueries(value, indexUrl) {
    const basic = candidates(value);
    if (basic.length === 1 && /[:*"()]|\b(?:AND|OR|NOT)\b/.test(value)) return [];
    const index = /\d/.test(value) ? await loadIndex(indexUrl) : null;
    const alternatives = [...(index ? catalogueCandidates(value, index) : []), ...basic.slice(1)];
    const seen = new Set([value.trim().toLowerCase()]);
    return alternatives.filter(query => {
      const key = query.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 4);
  }

  async function fetchText(url, parentSignal) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (parentSignal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    parentSignal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 2000);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Search ${response.status}`);
      return await response.text();
    } finally {
      clearTimeout(timer);
      parentSignal?.removeEventListener('abort', abort);
    }
  }

  async function resolve(value, load, signal, indexUrl) {
    const queries = candidates(value);
    // Warm the cached public lookup alongside the original code query.
    if (/\d/.test(value)) loadIndex(indexUrl);
    const original = await load(queries[0]);
    if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    if (original.count > 0) return original;
    const otherQueries = await alternateQueries(value, indexUrl);
    if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    if (!otherQueries.length) return original;
    // Alternate requests run together, only after an empty original response.
    const alternatives = await Promise.allSettled(otherQueries.map(load));
    if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    return alternatives.find(item => item.status === 'fulfilled' && item.value.count > 0)?.value || original;
  }

  async function recoverResults() {
    const marker = document.querySelector('[data-fdm-search-count="0"]');
    if (!marker) return;
    const current = new URL(window.location.href);
    const query = current.searchParams.get('q');
    // Never broaden an intentional filter, an advanced query, or a later page.
    if (!query || current.searchParams.get('_fdm_recovered') === '1' || current.searchParams.get('page') > 1 ||
        Array.from(current.searchParams.keys()).some(key => key.startsWith('filter.'))) return;
    const alternatives = await alternateQueries(query, marker.dataset.fdmSearchIndex);
    if (!alternatives.length) return;
    try {
      const results = await Promise.allSettled(alternatives.map(async candidate => {
        const url = new URL(current);
        url.searchParams.set('q', candidate);
        url.searchParams.set('section_id', marker.dataset.fdmSearchSection);
        const markup = await fetchText(url.toString());
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const count = parsed.querySelector('[data-fdm-search-count]');
        return { query: candidate, count: Number(count?.dataset.fdmSearchCount || 0) };
      }));
      const match = results.find(item => item.status === 'fulfilled' && item.value.count > 0)?.value;
      if (match && window.location.href === current.href) {
        current.searchParams.set('q', match.query);
        current.searchParams.set('_fdm_recovered', '1');
        // One replacement navigation; successful results stop this fallback.
        window.location.replace(current.toString());
      }
    } catch (error) {
      // Leave Shopify's original page and normal search available on failure.
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', recoverResults, { once: true });
  else recoverResults();
  return { candidates, catalogueCandidates, alternateQueries, fetchText, resolve };
})();

if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends BaseElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      connectedCallback() {
        super.connectedCallback();

        const input = this.input;
        if (!input) return;

        const hide = this.hide.bind(this);
        ['pointerdown', 'input'].forEach((event) => this.on(input, event, hide));
      }

      get input() {
        return this.closest('form')?.querySelector('input[type="search"]');
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      hide() {
        if (this.parentElement.hasAttribute('hidden')) return;
        this.parentElement.setAttribute('hidden', '');
      }

      async init() {
        if (this.initialized) return;
        this.initialized = true;

        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.textContent = text;
        if (!this.scrollWidth) {
          this.style.removeProperty('width');
          this.cursor.classList.add('blink');
          return;
        }

        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        if (!this.scrollWidth) return;

        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.textContent = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends BaseElementMixin(HTMLFormElement) {
      constructor() {
        super();

        this.cachedMap = new Map();
      }

      connectedCallback() {
        super.connectedCallback();

        this.focusElement = this.input;
        this.on(this, 'submit', this.onSubmit.bind(this));
        this.on(this.input, 'input', () => {
          this.renderAbortController?.abort();
          this.resolvedQuery = null;
          this.removeAttribute('loading');
        });
        this.on(this.resetButton, 'click', this.clear.bind(this));
        this.on(this.input, 'input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.on(this.input, 'focus', this.onFocus.bind(this));
        this.on(document, 'keydown', this.onKeydown.bind(this));
        this.on(this.input, 'input', this.updateResetButton.bind(this));
        this.updateResetButton();
      }

      disconnectedCallback() {
        super.disconnectedCallback();

        this.renderAbortController?.abort();
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get dropdownParent() {
        return this.closest('.collection') || this.closest('.search-inline');
      }

      get statusElement() {
        return this.querySelector('.search__status');
      }

      updateResetButton() {
        if (this.resetButton) this.resetButton.hidden = this.input.value.length === 0;
      }

      onFocus() {
        if (!this.dropdownParent) return;

        this.open();

        if (this.getQuery().length === 0) return;
        this.renderSection(this.buildUrl().toString());
      }

      onKeydown(event) {
        if (event.key !== 'Escape') return;

        if (event.target === this.input) event.preventDefault();

        if (!this.dropdownParent || !this.dropdownParent.classList.contains('search-active')) return;

        this.close();
        this.input.blur();
      }

      open() {
        if (!this.dropdownParent) return;

        document.querySelectorAll('.search-active').forEach((el) => {
          if (el !== this.dropdownParent) el.classList.remove('search-active');
        });
        this.dropdownParent.classList.add('search-active');
        document.body.classList.add('predictive-search-open');
      }

      close() {
        if (!this.dropdownParent) return;

        this.dropdownParent.classList.remove('search-active');
        document.body.classList.remove('predictive-search-open');
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.renderAbortController?.abort();
        this.resolvedQuery = null;
        this.removeAttribute('loading');
        this.input.value = '';
        this.updateResetButton();
        if (this.statusElement) this.statusElement.textContent = '';
        this.input.focus();
        this.removeAttribute('results');
      }

      getQuery() {
        return this.input.value.trim();
      }

      async onSubmit(event) {
        const query = this.getQuery();
        if (this.submitting) {
          event.preventDefault();
          return;
        }
        if (this.resolvedQuery?.input === query) {
          this.input.value = this.resolvedQuery.query;
          return;
        }
        // Finish resolving code searches even when Enter precedes the debounce.
        // Ordinary text and advanced expressions retain normal submission.
        if (!/\d/.test(query) || /[:*"()]|\b(?:AND|OR|NOT)\b/.test(query)) return;
        event.preventDefault();
        this.submitting = true;
        this.submitQuery = query;
        try {
          await this.renderSection(this.buildUrl().toString());
          if (query !== this.getQuery() || !this.isConnected) return;
          if (this.resolvedQuery?.input === query) this.input.value = this.resolvedQuery.query;
          HTMLFormElement.prototype.submit.call(this);
        } finally {
          this.submitting = false;
          this.submitQuery = null;
        }
      }

      buildUrl(query = this.getQuery()) {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit'), 10) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      onChange() {
        if (this.submitting && this.submitQuery === this.getQuery()) return;
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.open();

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      renderUnsupportedHint() {
        if (!theme.strings.searchUnsupportedLocale?.length) {
          this.close();
          this.removeAttribute('results');
          return;
        }

        const target = this.querySelector('[id^="PredictiveSearchResults-"]');
        if (target) {
          target.replaceChildren();
          const p = document.createElement('p');
          p.className = 'predictive-search__hint text-sm leading-tight';
          p.textContent = theme.strings.searchUnsupportedLocale;
          target.appendChild(p);
        }

        this.setAttribute('results', '');
      }

      async renderSection(url) {
        if (!theme.config.predictiveSearch) {
          this.renderUnsupportedHint();
          return;
        }
        this.renderAbortController?.abort();
        const controller = new AbortController();
        this.renderAbortController = controller;
        const input = this.getQuery();
        this.setAttribute('loading', '');
        try {
          const result = await window.FDMSearch.resolve(input, async (query) => {
            const key = this.buildUrl(query).toString();
            let markup = this.cachedMap.get(key);
            if (markup === undefined) {
              markup = await window.FDMSearch.fetchText(key, controller.signal);
              this.cachedMap.set(key, markup);
              if (this.cachedMap.size > 50) this.cachedMap.delete(this.cachedMap.keys().next().value);
            }
            const parsed = new DOMParser().parseFromString(markup, 'text/html');
            const target = parsed.getElementById(this.querySelector('[id^="PredictiveSearchResults-"]').id);
            if (!target) throw new Error('Missing predictive search section');
            return { query, markup, count: Number(target.dataset.fdmProductCount || 0) };
          }, controller.signal, this.dataset.fdmSearchIndex);
          if (controller.signal.aborted || input !== this.getQuery()) return;
          this.resolvedQuery = { input, query: result.query };
          this.renderSearchResults(result.markup);
          this.setAttribute('results', '');
        } catch (error) {
          if (controller.signal.aborted) return;
          if (error.message.includes('417')) {
            theme.config.predictiveSearch = false;
            this.renderUnsupportedHint();
          } else {
            this.removeAttribute('results');
            if (error.name !== 'AbortError') console.error(error);
          }
        } finally {
          if (this.renderAbortController === controller) this.removeAttribute('loading');
        }
      }

      renderSearchResults(responseText) {
        const target = this.querySelector('[id^="PredictiveSearchResults-"]');
        if (!target) return;

        const parsed = new DOMParser().parseFromString(responseText, 'text/html').getElementById(target.id);
        if (parsed) target.replaceChildren(...parsed.childNodes);

        this.announceResults(target);
      }

      announceResults(target) {
        if (!this.statusElement) return;

        const count = target.querySelectorAll(':scope > div > ul > li, .horizontal-product').length;
        const template = count === 0 ? theme.strings.searchNoResults : (count === 1 ? theme.strings.searchResultsOne : theme.strings.searchResultsOther);
        this.statusElement.textContent = template.replace('[count]', count).replace('[terms]', this.getQuery());
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      connectedCallback() {
        super.connectedCallback();

        this.on(this, 'click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          const region = this.closest('.search-active');
          if (!region) return;

          region.classList.remove('search-active');
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}

if (!customElements.get('voice-search')) {
  customElements.define(
    'voice-search',
    class VoiceSearch extends MagnetButton {
      constructor() {
        super();

        this.SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      }

      connectedCallback() {
        super.connectedCallback();

        if (!this.SpeechRecognition) return;

        this.input = this.closest('form')?.querySelector('input[type="search"]');
        if (!this.input) return;

        // Safari: OS voice service is unavailable — keep the icon hidden this session
        if (theme.config.hasSessionStorage && window.sessionStorage.getItem(`${theme.settings.themeName}:voice-unavailable`)) {
          this.setAttribute('hidden', '');
          return;
        }

        this.syncMicrophonePermission();
        this.on(this, 'click', this.onClick.bind(this));

      }

      disconnectedCallback() {
        super.disconnectedCallback();

        this.recognition?.abort?.();
      }

      async syncMicrophonePermission() {
        try {
          const status = await navigator.permissions.query({ name: 'microphone' });
          const sync = () => this.toggleAttribute('hidden', status.state === 'denied' || this.unavailable === true);
          sync();
          this.on(status, 'change', sync);
        } catch (e) {
          this.removeAttribute('hidden');
        }
      }

      onClick(event) {
        event.preventDefault();

        if (this.listening) {
          this.recognition?.stop();
          return;
        }

        this.recognition = new this.SpeechRecognition();
        this.recognition.lang = document.documentElement.lang || 'en';
        this.recognition.interimResults = false;
        this.recognition.maxAlternatives = 1;

        this.recognition.addEventListener('result', this.onResult.bind(this));
        this.recognition.addEventListener('end', this.onEnd.bind(this));
        this.recognition.addEventListener('error', this.onEnd.bind(this));

        this.listening = true;
        this.classList.add('listening');
        this.recognition.start();
      }

      onResult(event) {
        const transcript = Array.from(event.results)
          .map((result) => result[0].transcript)
          .join('');

        this.input.value = transcript;
        this.input.focus();
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
      }

      onEnd(event) {
        this.listening = false;
        this.classList.remove('listening');

        // Safari: the OS voice service is unavailable
        if (event.error === 'service-not-allowed') this.markUnavailable();
      }

      markUnavailable() {
        this.unavailable = true;
        if (theme.config.hasSessionStorage) {
          window.sessionStorage.setItem(`${theme.settings.themeName}:voice-unavailable`, 'true');
        }

        this.closest('form')?.querySelector('[data-voice-unavailable]')?.removeAttribute('hidden');
        this.setAttribute('hidden', '');
      }
    }, { extends: 'button' }
  );
}
